import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    MenuItem,
    Typography
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import LayoutAdmin from "../components/LayoutAdmin";
import axiosExt from "../axios";
import { API_BASE_URL } from "../config";
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';

import { Customer, ServicePrice } from '../types';

const ServicePricingPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const serviceId = searchParams.get("serviceId");

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [prices, setPrices] = useState<ServicePrice[]>([]);
    const [defaultPrice, setDefaultPrice] = useState<number | "">("");
    const [defaultPriceAfter, setDefaultPriceAfter] = useState<number | "">("");

    const [loading, setLoading] = useState(false);
    const [serviceName, setServiceName] = useState<string>("");

    const { enqueueSnackbar } = useSnackbar();

    // dialog
    const [open, setOpen] = useState(false);
    const [newCustomerId, setNewCustomerId] = useState("");
    const [newPrice, setNewPrice] = useState("");
    //const [newPriceAfter, setNewPriceAfter] = useState("");

    
    const [deleteRow, setDeleteRow] = useState<ServicePrice | null>(null);

    // ---------------------------
    // FETCH DATA
    // ---------------------------
    useEffect(() => {
        if (!serviceId) return;
        fetchData();
    }, [serviceId]);

    const fetchData = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem("token");

            const [serviceRes, customersRes, pricesRes] = await Promise.all([
                axiosExt.get(`${API_BASE_URL}/services/${serviceId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axiosExt.get(`${API_BASE_URL}/customers`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axiosExt.get(
                    `${API_BASE_URL}/service-prices/by-service/${serviceId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                )
            ]);

            setCustomers(customersRes.data);
            setServiceName(serviceRes.data.name);

            const data: ServicePrice[] = pricesRes.data;

            // split default vs customer prices
            const defaultP = data.find(p => !p.customerId);
            const customerPrices = data.filter(p => p.customerId);

            setDefaultPrice(defaultP?.price ?? "");
            setDefaultPriceAfter(defaultP?.pricePerUnitAfter1stUnit ?? 0);
            setPrices(customerPrices);

        } catch (err) {
            alert("Error loading data");
            console.error("Error loading pricing:", err);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------
    // SAVE DEFAULT
    // ---------------------------
    const saveDefaultPrice = async () => {
        const token = localStorage.getItem("token");

        try {
            await axiosExt.post(
                `${API_BASE_URL}/service-prices/upsert`,
                {
                    serviceId,
                    customerId: null,
                    price: defaultPrice,
                    pricePerUnitAfter1stUnit: defaultPriceAfter 
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            enqueueSnackbar(`Pricing saved`,
                {
                    variant: "success",
                    autoHideDuration: 5000,
                });
        }
        catch (err) {
            enqueueSnackbar(`Error saving prices`,
                {
                    variant: "error",
                    autoHideDuration: 5000,
                });
        }
    };

    // ---------------------------
    // INLINE EDIT CUSTOMER PRICE
    // ---------------------------
    const handlePriceChange = (id: string, value: string) => {
        const numeric = parseFloat(value);

        setPrices(prev =>
            prev.map(p =>
                p.id === id
                    ? { ...p, price: isNaN(numeric) ? 0 : numeric }
                    : p
            )
        );
    };

    //const handlePriceAfterChange = (id: string, value: string) => {
    //    const numeric = parseFloat(value);

    //    setPrices(prev =>
    //        prev.map(p =>
    //            p.id === id
    //                ? { ...p, pricePerUnitAfter1stUnit: isNaN(numeric) ? 0 : numeric }
    //                : p
    //        )
    //    );
    //};

    const saveRow = async (row: ServicePrice) => {
        const token = localStorage.getItem("token");
        try {
            await axiosExt.post(
                `${API_BASE_URL}/service-prices/upsert`,
                row,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            enqueueSnackbar(`Pricing saved`,
                {
                    variant: "success",
                    autoHideDuration: 5000,
                });
        }
        catch (err) {
            enqueueSnackbar(`Error saving prices`,
                {
                    variant: "error",
                    autoHideDuration: 5000,
                });
        }
    };

    // ---------------------------
    // DELETE
    // ---------------------------
    const handleDelete = async (row: ServicePrice) => {
            const token = localStorage.getItem("token");

        try {
            await axiosExt.delete(
                `${API_BASE_URL}/service-prices/${row.id}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setPrices(prev => prev.filter(p => p.id !== row.id));
            enqueueSnackbar(`Pricing deleted`, {
                variant: "success",
                autoHideDuration: 5000,
            });
        }
        catch (err) {
            enqueueSnackbar(`Error deleting prices`, {
                variant: "error",
                autoHideDuration: 5000,
            });
        }
    };

    // ---------------------------
    // ADD CUSTOMER PRICE
    // ---------------------------
    const handleAdd = async () => {
        const token = localStorage.getItem("token");
        try {
            await axiosExt.post(
                `${API_BASE_URL}/service-prices/upsert`,
                {
                    serviceId,
                    customerId: newCustomerId,
                    price: parseFloat(newPrice),
                    pricePerUnitAfter1stUnit: 0, //parseFloat(newPriceAfter) || 0
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setOpen(false);
            setNewCustomerId("");
            setNewPrice("");
            fetchData();
            enqueueSnackbar(`Pricing saved`,
                {
                    variant: "success",
                    autoHideDuration: 5000,
                });
        }
        catch (err) {
            enqueueSnackbar(`Error saving prices`,
                {
                    variant: "error",
                    autoHideDuration: 5000,
                });
        }
    };

    // ---------------------------
    // GRID
    // ---------------------------
    const columns: GridColDef[] = [
        {
            field: "customerId",
            headerName: t("Customer"),
            width: 200,
            valueGetter: (params) => {
                const c = customers.find(x => x.id === params);
                return c?.name || "";
            }
        },
        {
            field: "price",
            headerName: t("Price"),
            width: 150,
            renderCell: (params) => (
                <TextField
                    size="small"
                    type="number"
                    value={params.row.price}
                    onChange={(e) =>
                        handlePriceChange(params.row.id, e.target.value)
                    }
                    onBlur={() => saveRow(params.row)}
                />
            )
        },
        //{
        //    field: "pricePerUnitAfter1stUnit",
        //    headerName: t("After 1st Unit"),
        //    width: 180,
        //    renderCell: (params) => (
        //        <TextField
        //            size="small"
        //            type="number"
        //            value={params.row.pricePerUnitAfter1stUnit ?? 0}
        //            onChange={(e) =>
        //                handlePriceAfterChange(params.row.id, e.target.value)
        //            }
        //            onBlur={() => saveRow(params.row)}
        //        />
        //    )
        //},
        {
            field: "actions",
            headerName: t("Actions"),
            width: 150,
            renderCell: (params) => (
                <Button
                    color="error"
                    onClick={() => setDeleteRow(params.row)}
                >
                    {t("Delete")}
                </Button>
            )
        }
    ];

    // ---------------------------
    // RENDER
    // ---------------------------
    return (
        <LayoutAdmin>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        {t("Service Pricing")}
                        {serviceName ? ` - ${serviceName}` : ""}
                    </Typography>
                </Box>
                {/* DEFAULT PRICE */}
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body1">
                        {t("Default Price")}
                    </Typography>
                    <TextField
                        type="number"
                        value={defaultPrice}
                        onChange={(e) => setDefaultPrice(parseFloat(e.target.value))}
                    />
                    {/*<TextField*/}
                    {/*    type="number"*/}
                    {/*    value={defaultPriceAfter}*/}
                    {/*    onChange={(e) => setDefaultPriceAfter(parseFloat(e.target.value))}*/}
                    {/*    label={t("After 1st Unit")}*/}
                    {/*/>*/}
                    <Button variant="contained" onClick={saveDefaultPrice} size="small" sx={{ maxHeight: 24 }}>
                        {t("Save")}
                    </Button>
                </Box>

                {/* CUSTOMER PRICES */}
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body1">
                        {t("Customer Prices")}
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => setOpen(true)}
                        size="small"
                        sx={{ maxHeight:24} }
                    >
                        {t("Add")}
                    </Button>
                </Box>

                <DataGrid
                    rows={prices}
                    columns={columns}
                    getRowId={(r) => r.id}
                    loading={loading}
                    autoHeight
                />

                {/* ADD DIALOG */}
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{t("Add Customer Price")}</DialogTitle>
                    <DialogContent>
                        <TextField
                            select
                            label={t("Customer")}
                            fullWidth
                            value={newCustomerId}
                            onChange={(e) => setNewCustomerId(e.target.value)}
                            margin="dense"
                        >
                            {customers.map(c => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label={t("Price")}
                            type="number"
                            fullWidth
                            value={newPrice}
                            onChange={(e) => setNewPrice(e.target.value)}
                            margin="dense"
                        />
                        {/*<TextField*/}
                        {/*    label={t("After 1st Unit")}*/}
                        {/*    type="number"*/}
                        {/*    fullWidth*/}
                        {/*    value={newPriceAfter}*/}
                        {/*    onChange={(e) => setNewPriceAfter(e.target.value)}*/}
                        {/*    margin="dense"*/}
                        {/*/>*/}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>
                            {t("Cancel")}
                        </Button>
                        <Button onClick={handleAdd}>
                            {t("Save")}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>

            <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)}>
                <DialogTitle>{t("Confirm Delete")}</DialogTitle>
                <DialogContent>
                    {t("Are you sure you want to delete this item?")}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteRow(null)}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            if (!deleteRow) return;

                            await handleDelete(deleteRow);
                            setDeleteRow(null);
                        }}
                    >
                        {t("Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </LayoutAdmin>
    );
};

export default ServicePricingPage;